import { Component } from '@theme/component'
import {
  getPageScrollY,
  isPageScrollLocked,
  navigationMayRestoreScroll
} from '@theme/dialog'
import { StandardEvents } from '@shopify/events'
import { morph } from '@theme/morph'
import { triggerFlyToCart } from '@theme/fly-to-cart'
import { yieldToMain } from '@theme/utilities'

/**
 * @param {{ isInViewport: boolean, isSectionLeavingViewport?: boolean, scrollY: number, footerContentVisible?: boolean, scrollLocked?: boolean, chatActive?: boolean, variantAvailable?: boolean }} state
 * @returns {boolean}
 */
export function shouldShowStickyBar({
  isInViewport,
  isSectionLeavingViewport = true,
  scrollY,
  footerContentVisible = false,
  scrollLocked = false,
  chatActive = false,
  variantAvailable = true
}) {
  if (
    scrollLocked ||
    chatActive ||
    !variantAvailable ||
    !isSectionLeavingViewport
  )
    return false
  return !isInViewport && scrollY > 0 && !footerContentVisible
}

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends Component {
    /** @type {HTMLElement | null} */
    #section = null

    /** @type {HTMLElement | null} */
    #productInformation = null

    /** @type {IntersectionObserver | null} */
    #footerObserver = null

    /** @type {AbortController | null} */
    #lifecycleController = null

    /** Hide sticky bar while main footer grid (.footer-content) is in view */
    #footerContentVisible = false

    constructor() {
      super()
      this.isVisible = false
      this.onScroll = this.onScroll.bind(this)
      this.onButtonClick = this.onButtonClick.bind(this)
    }

    connectedCallback() {
      this.triggerSelector =
        this.dataset.thresholdSelector ||
        '.product-info-wrapper .product-form-buttons, .product-form-buttons, .product-form__buttons, .product-form'

      this.#section = this.closest('.shopify-section, dialog')
      this.#productInformation =
        this.closest('.product-information') ||
        this.#section?.querySelector('.product-information') ||
        this.#section

      window.addEventListener('scroll', this.#handleScroll, { passive: true })

      const btn = this.querySelector('[data-action="add-to-cart"]')
      if (btn) btn.addEventListener('click', this.onButtonClick)

      this.#section?.addEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect
      )

      this.#lifecycleController = new AbortController()
      const { signal } = this.#lifecycleController

      customElements.whenDefined('shopify-chat').then(() => {
        if (signal.aborted) return
        if (this.isVisible && this.#isChatActive()) {
          this.isVisible = false
          this.classList.remove('is-visible')
          this.setAttribute('aria-hidden', 'true')
        }
      })

      const footerContent = document.querySelector('.footer-content')
      if (footerContent) {
        this.#footerObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0]
            if (!entry) return
            this.#footerContentVisible = entry.isIntersecting
            if (this.#hasScrolled || this.isVisible) this.onScroll()
          },
          { root: null, threshold: 0 }
        )
        this.#footerObserver.observe(footerContent)
      }

      // The bar needs scrollY > 0, so a fresh load has nothing to measure (each
      // measurement forces a full-page layout while the page is still loading).
      // A restored offset is placed before the slide transition is enabled.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.#hasScrolled || navigationMayRestoreScroll()) this.onScroll()
          this.classList.add('sticky-atc--animatable')
        })
      })
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.#handleScroll)
      if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame)
      this.#scrollFrame = 0

      this.#lifecycleController?.abort()
      this.#lifecycleController = null

      this.#footerObserver?.disconnect()
      this.#footerObserver = null

      this.#section?.removeEventListener(
        StandardEvents.productSelect,
        this.#handleProductSelect
      )
      this.#productInformation = null

      const btn = this.querySelector('[data-action="add-to-cart"]')
      if (btn) btn.removeEventListener('click', this.onButtonClick)
    }

    /** @type {number} */
    #scrollFrame = 0

    /** @type {boolean} */
    #hasScrolled = false

    /** Layout is read at most once per frame instead of on every scroll event. */
    #handleScroll = () => {
      this.#hasScrolled = true
      if (this.#scrollFrame) return
      this.#scrollFrame = requestAnimationFrame(() => {
        this.#scrollFrame = 0
        this.onScroll()
      })
    }

    onScroll() {
      if (isPageScrollLocked()) return

      /** @type {HTMLElement | null} */
      let trigger = document.querySelector(this.triggerSelector)

      if (!trigger) {
        trigger = document.querySelector(
          'form[action*="/cart/add"] [type="submit"]'
        )
      }

      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight
      const isInViewport = rect.bottom > 0 && rect.top < viewportHeight
      const sectionEl = this.#productInformation || this.#section
      const sectionRect = sectionEl ? sectionEl.getBoundingClientRect() : null
      const isSectionLeavingViewport = sectionRect
        ? sectionRect.bottom <= viewportHeight
        : true
      // Use getPageScrollY() (not window.scrollY): while the page scroll is
      // locked the body is position:fixed and window.scrollY reads 0, which
      // would wrongly hide the bar. getPageScrollY() returns the real offset.
      const shouldShow = shouldShowStickyBar({
        isInViewport,
        isSectionLeavingViewport,
        scrollY: getPageScrollY(),
        footerContentVisible: this.#footerContentVisible,
        chatActive: this.#isChatActive(),
        variantAvailable: this.dataset.variantAvailable !== 'false'
      })

      if (shouldShow !== this.isVisible) {
        this.isVisible = shouldShow
        this.classList.toggle('is-visible', shouldShow)
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true')
      }
    }

    /**
     * Inbox chat UI overlaps the sticky bar when the custom element is registered.
     *
     * @returns {boolean}
     */
    #isChatActive() {
      if (!customElements.get('shopify-chat')) return false
      return Boolean(document.querySelector('shopify-chat'))
    }

    /**
     * Trigger the canonical PDP add-to-cart submission flow.
     *
     * Always routes through `form.requestSubmit(button?)` so the theme's
     * `<product-form-component>` `submit` listener runs and owns the request
     * (multi-item JSON payload, single-flight guard, drawer-open on success).
     * Never falls back to `form.submit()` because the native submit bypasses
     * the listener and lands the user on `/cart`.
     *
     * @param {Event} e
     */
    onButtonClick(e) {
      e.preventDefault()

      const stickyButton = /** @type {HTMLButtonElement | null} */ (
        e.currentTarget instanceof HTMLButtonElement ? e.currentTarget : null
      )
      if (stickyButton?.disabled) return

      const section = this.#section || document
      /** @type {HTMLFormElement | null} */
      let form =
        section.querySelector('product-form-component form') ||
        section.querySelector('form[action*="/cart/add"]')

      if (!form) {
        const formId = this.dataset.productFormId
        if (formId) {
          const candidate = document.getElementById(formId)
          if (candidate instanceof HTMLFormElement) form = candidate
        }
      }

      if (!form) {
        console.warn(
          '[sticky-add-to-cart] No PDP form found; refusing to submit to avoid /cart navigation.'
        )
        return
      }

      /** @type {HTMLButtonElement | null} */
      const submitter =
        form.querySelector('.product-form__submit, [name="add"]') ||
        form.querySelector('button[type="submit"]')

      if (typeof form.requestSubmit === 'function') {
        const addToCartComponent = section.querySelector(
          'add-to-cart-component'
        )
        const stickyImage = section.querySelector('.sticky-atc__image')
        const imageUrl =
          addToCartComponent?.dataset.productVariantMedia ||
          (stickyImage instanceof HTMLImageElement
            ? stickyImage.currentSrc || stickyImage.src
            : undefined)

        triggerFlyToCart({ source: stickyButton, imageUrl })

        if (submitter) {
          form.requestSubmit(submitter)
        } else {
          form.requestSubmit()
        }
        return
      }

      // requestSubmit is part of the modern HTMLFormElement API. If it's
      // missing (very old browser), synthesize a bubbling submit event so the
      // <product-form-component> listener still runs. We deliberately do not
      // call `form.submit()` here because the native call bypasses listeners
      // and navigates away from the PDP.
      form.dispatchEvent(
        new Event('submit', { bubbles: true, cancelable: true })
      )
    }

    /**
     * @param {import('@shopify/events').ProductSelectEvent} event
     */
    #handleProductSelect = (event) => {
      if (
        !(event.target instanceof Element) ||
        event.target.closest('product-card')
      )
        return

      event.promise
        ?.then(async ({ detail }) => {
          if (!detail?.html) return

          const { html, productId } = detail

          if (productId && productId !== this.dataset.productId) return

          const newStickyAtc = html.querySelector('sticky-add-to-cart')
          if (!newStickyAtc) return

          if (newStickyAtc instanceof HTMLElement) {
            const available = newStickyAtc.dataset.variantAvailable
            if (available != null) {
              this.dataset.variantAvailable = available
            }
          }

          const newContainer = newStickyAtc.querySelector(
            '.sticky-atc__container'
          )
          const currentContainer = this.querySelector('.sticky-atc__container')
          if (!currentContainer || !newContainer) return

          await yieldToMain()
          if (!this.isConnected) return

          morph(currentContainer, newContainer)

          const btn = this.querySelector('[data-action="add-to-cart"]')
          if (btn) {
            btn.removeEventListener('click', this.onButtonClick)
            btn.addEventListener('click', this.onButtonClick)
          }

          this.onScroll()
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[sticky-add-to-cart] Event promise rejected:', error)
          }
        })
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart)
}
